import React, { useContext, useEffect, useState } from 'react'
import styles from './fixed-trips-details.module.scss'
import Button from 'src/components//button/Button'
import Input from 'src/components//input/Input'
import { StateContext } from 'src/AppContext'
import { baseApiURL } from 'src/utils/utils'
import { IState, IAttributes, IAddress, IAccount } from 'src/Interface'
import axios from 'axios'
import AutocompletePoi from 'src/components/autocomplete/AutocompletePoi'
import DropDown from 'src/components/dropDown/Dropdown'
import { format } from 'date-fns/esm'
import { toast } from 'react-toastify'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import { gaEvenActions, gaEvent, gaEventCategories } from 'src/utils/ga/ga'
import { gaFixedTripMapper, gaUserMapper } from 'src/utils/ga/gaMapper'

interface IValues {
  destinationAddress?: IAddress
  pickupAddress?: IAddress,
  comment?: string
  project?: string
  numberOfPeople?: number
  numberOfCars?: number,
  name?: string
  attributes?: Array<IAttributes>
  customerName?: string
  account: IAccount
  accountId: number,
  customerPhone?: string
  reference?: string
  price: string
  title: string,
  time: string,
  date: string
}

interface IProps {
  close?: any
  fixedTripId: number,
  branchId: number,
  companyId: number
}

const OrdersDetails: React.FunctionComponent<IProps> = ({ close, fixedTripId, branchId, companyId }: IProps) => {
  const state: IState = useContext(StateContext)
  const [accounts, setAccounts] = useState([])
  const dispatch = useContext(DispatchContext)
  const [loading, setLoading] = useState(true)
  const [selectedAttributes, updateSelectedAttributes] = useState([])
  const [bookingModule, setBookingModule] = useState({
    attributes: [],
    fields: [],
    referenceLabel: '',
    updatedAt: ''
  })

  const { cancellablePromise } = useCancellablePromise()

  const [values, setValues] = useState<IValues>({
    time: format(new Date(), 'HH:mm'),
    date: format(new Date(), 'yyyy-MM-dd'),
    numberOfPeople: 4,
    destinationAddress: null,
    pickupAddress: null,
    comment: '',
    project: '',
    customerName: '',
    price: '',
    accountId: null,
    title: '',
    numberOfCars: 1,
    customerPhone: '',
    account: {
      id: null,
      name: null,
      code: null
    },
    reference: '',
  })

  const updateFixedTrip = () => {
    if (state.user.accessToken) {
      axios.put(`${baseApiURL()}/branches/${branchId}/fixed_trips/${fixedTripId}`,
        {
          title: values.title ? values.title : null,
          price: values.price,
          pickupAddress: values.pickupAddress ? values.pickupAddress : null,
          destinationAddress: values.destinationAddress ? values.destinationAddress : null,
          reference: values.reference ? values.reference : null,
          project: values.project ? values.project : null,
          comment: values.comment ? values.comment : null,
          customerName: values.customerName ? values.customerName : null,
          customerPhone: values.customerPhone ? values.customerPhone : null,
          accountId: values.accountId ? values.accountId : null,
          numberOfPeople: values.numberOfPeople ? values.numberOfPeople : null,
          attributeIds: selectedAttributes.length > 0 ? selectedAttributes : null,
          bookingModuleUpdatedAt: bookingModule.updatedAt
        }, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        return null
      }).then(() => {
        return axios(`${baseApiURL()}/branches/${branchId}/fixed_trips/admin`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
      }).then((result) => {
        dispatch({ type: 'SET_FIXED_TRIPS', fixedTrips: result.data })
        close()
      }).catch((err: any) => {
        toast(err.response.data.message)
      })
    }
  }

  useEffect(() => {
    if (state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/accounts`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
        .then((result) => {
          setAccounts([{ label: 'ingen konto', value: '' }, ...result.data.filter(item => item.status === "ACTIVE").map(item => ({ label: item.name, value: item.id }))])
        }).catch((err) => {
          toast(err.response.data.message)
        }))
    }
  }, [cancellablePromise, companyId, state.user.accessToken])

  useEffect(() => {
    cancellablePromise(axios(`${baseApiURL()}/companies/${companyId}/booking_module`, {
      headers: {
        Authorization:
          `Bearer ${state.user.accessToken}`
      }
    })
      .then((result) => {
        setBookingModule({ attributes: result.data.attributesFields, fields: result.data.staticFields, updatedAt: result.data.updatedAt, referenceLabel: result.data.referenceLabel })
      }).catch((err) => {
        toast(err.response.data.message)
      }))
  }, [companyId, fixedTripId, branchId, state.user.accessToken, cancellablePromise])

  useEffect(() => {
    if (bookingModule.attributes.length)
      cancellablePromise(axios(`${baseApiURL()}/branches/${branchId}/fixed_trips/${fixedTripId}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(({ data }: any) => {
        setValues(data)
        setLoading(false)
        updateSelectedAttributes(bookingModule.attributes.filter(val =>
          data.attributes.includes(val.id) ? true : val.required).map(attribute => attribute.id))
      }).catch((err) => {
        toast(err.response.data.message)
      }))
  }, [bookingModule.attributes, branchId, cancellablePromise, fixedTripId, state.user.accessToken])

  const handleInputChange = (fieldName: string) => (value: string) => {
    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const toggleSelectedAddons = (id: any) => {
    if (selectedAttributes.includes(id)) {
      const attributes = [...selectedAttributes]
      const index = selectedAttributes.findIndex(a => a === id)
      attributes.splice(index, 1)
      updateSelectedAttributes(attributes)
    } else {
      updateSelectedAttributes([...selectedAttributes, id])
    }
  }

  const deleteFixedTrip = () => {
    axios.delete(`${baseApiURL()}/branches/${branchId}/fixed_trips/${fixedTripId}`,
      {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then(() => {
        return null
      }).then(() => {
        return axios(`${baseApiURL()}/branches/${branchId}/fixed_trips/admin`, {
          headers: {
            Authorization:
              `Bearer ${state.user.accessToken}`
          }
        })
      }).then((result) => {
        gaEvent(gaEventCategories.fixed_trips, gaEvenActions.removed_fixed_trip, gaUserMapper(state), gaFixedTripMapper(values))
        dispatch({ type: 'SHOW_MODAL', asideType: '' })
        dispatch({ type: 'SET_FIXED_TRIPS', fixedTrips: result.data })
        close()
      }).catch((err) => {
        setLoading(false)
        toast(err.response.data.message)
      })

  }

  const detailFields = bookingModule.fields.filter((item) => item.category === 'DETAILS' && item.code !== 'DA' && item.code !== 'TI')
  const customerFields = bookingModule.fields.filter((item) => item.category === 'CUSTOMER')
  const requiredFields = [...customerFields, ...detailFields].filter(field => field.required && field.code !== 'FI' && field.code !== 'BE').map(field => field.code)

  const requiredFieldsLength = requiredFields.filter(code => {
    const fields = [...state.bookingModule.detailFields.list, ...state.bookingModule.customerFields.list]
    const matchedField = fields.find(field => field.code === code)
    return matchedField ? !values[matchedField.name] : false
  }).length

  if (loading) {
    <div>Loading</div>
  }

  return (
    <>
      <header className={styles.header}>
        <h3>Opret fast tur</h3>
      </header>
      <div className={styles.container}>
        <div className={styles.section}>
          {state.bookingModule.detailFields.list.filter((propety) => detailFields.find((item) => propety.code === item.code))
            .map((item, index) => {
              switch (item.type) {
                case 'autocomplete':
                  return <AutocompletePoi
                    key={index}
                    completedAddress={values[item.name]}
                    label={`
                        ${item.inputLabel || item.description}
                         ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                    placeholder={item.placeholder}
                    iconType={item.name === 'pickupAddress' ? 'pickup' : 'destination'}
                    modifiers={`tw-mb-5 ${item.modifier ? item.modifier : ''} `}
                    setAddress={() => handleInputChange(item.name)}
                    name={item.name} />
                case 'dropdownInput':
                  return <DropDown
                    key={index}
                    label={`
                        ${item.inputLabel || item.description}
                         ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                    modifiers={`tw-mb-5 ${item.modifier ? item.modifier : ''} `}
                    curried={true}
                    value={values.numberOfPeople.toString()}
                    options={item.dropDownOptions}
                    handleOnChange={() => handleInputChange(item.name)}
                  />
                default:
                  return <Input
                    key={index}
                    placeholder={item.placeholder}
                    modifiers={`tw-mb-3 tw-mr- 3 tw-last:mr-0 ${item.modifier ? item.modifier : ''} `}
                    type={item.type}
                    value={values[item.name]}
                    min={format(new Date(), item.type === 'date' ? 'yyyy-MM-dd' : 'HH:mm')}
                    handleInputChange={() => handleInputChange(item.name)}
                    IconRight={item.IconRight}
                    countryCode={item.countryCode}
                    label={`
                        ${item.inputLabel || item.description}
                         ${detailFields.find((field) => field.code === item.code).required ? '*' : ''}`}
                    name='' />
              }
            })}
          {!detailFields.find((item) => 'DE' === item.code) && <AutocompletePoi
            label='Destination *'
            placeholder='Vælg destination'
            iconType='destination'
            modifiers='tw-mb-5 tw-flex-full'
            setAddress={() => handleInputChange('destinationAddress')}
            completedAddress={values.destinationAddress}
            name='destinationAddress'
          />}
          <div className='tw-flex tw-w-full'>
            <Input
              placeholder='Titel på fast tur'
              type='text'
              modifiers='tw-flex-3 tw-mr-3'
              value={values.title}
              handleInputChange={() => handleInputChange('title')}
              label='Titel *'
              name='title' />
            <Input
              placeholder='Pris'
              type='text'
              value={values.price}
              handleInputChange={() => handleInputChange('price')}
              currency='kr.'
              modifiers='tw-flex-1 '
              label='Fast pris'
              name='price'
            />
          </div>
        </div>
        {bookingModule.attributes.filter(attribute => !attribute.required).length > 0 && <div className={styles.section}>
          <p className='tw-flex-full tw-mb-3'>Tilvalg</p>
          {bookingModule.attributes.filter(attribute => !attribute.required).map((item, index) => (
            <Button
              modifiers='tw-ml-3 first:ml-0 tw-mb-3'
              handleClick={() => toggleSelectedAddons(item.id)}
              label={item.description}
              type={(selectedAttributes.includes(item.id)) ? 'addonActive' : 'addon'}
              key={index} />
          ))}
        </div>}
        <div className={styles.section}>
          <h6 className='tw-flex-full tw-mb-3'>Kunde informationer</h6>
          {accounts && accounts.length > 1 && <DropDown placeholder='Vælg konto' value={values.accountId || values.account && values.account.id} label='Konto' modifiers='tw-flex-full tw-mb-5' options={accounts} handleOnChange={() => handleInputChange('accountId')} curried={true} />}
          {state.bookingModule.customerFields.list.filter((propety) => customerFields.find((item) => propety.code === item.code))
            .map((item, index) => {
              if (item.type === 'dropdown') {
                return <DropDown
                  key={index}
                  label={`
                      ${item.inputLabel || item.description}
                      ${customerFields.find((field) => field.code === item.code).required ? '*' : ''} `}
                  modifiers={`tw-mb-5 tw-mr-3 ${item.modifier ? item.modifier : ''} `}
                  curried={true}
                  value={values.accountId ? 'card' : 'cash'}
                  options={item.dropDownOptions}
                />
              }

              return <div key={index} className={`tw-mr- 3 tw-mb-3 ${item.modifier ? item.modifier : ''} `}>
                <Input
                  placeholder={item.placeholder}
                  type='text'
                  isAllowOnlyDigits={item.name ==='customerPhone'}
                  value={values[item.name]}
                  handleInputChange={() => handleInputChange(item.name)}
                  IconRight={item.IconRight}
                  countryCode={item.countryCode}
                  label={`
                  ${item.code === 'RE' ? bookingModule.referenceLabel : item.inputLabel || item.description}
                    ${customerFields.find((field) => field.code === item.code).required ? '*' : ''} `}
                  name={item.name} />
              </div>
            })}
        </div>
      </div>
      <footer className={styles.footer}>
        <Button type='destructive' label='Fjern' modifiers='tw-mr-5 tw-ml-auto' handleClick={() => dispatch({ type: 'SHOW_MODAL', modalType: 'deleteConfirmation', showModal: true, title: 'Fjern faste tur', text: '<p>Er du sikker på at du vil fjerne denne faste tur?</p> <p>Dette kan ikke fortrydes.</p>', buttonText: 'Fjern faste tur', handleDelete: deleteFixedTrip })} />
        <Button disabled={!!requiredFieldsLength || !values.title} type='primary' label='Gem ændringer' handleClick={updateFixedTrip} />
      </footer>
    </>
  )
}

export default OrdersDetails
